import React, { useMemo } from "react";
import NoData from "../../common/NoData";
import useThemeColors from "../../organization/portal/useThemeColors";
import { alphaFromHex } from "../../../util/colors";
import useTranslation from "../../../i18n/useTranslation";

export default function Baseball({ groupName, stageName, standings }) {
    const colors = useThemeColors();
    const { t } = useTranslation();

    const scopeId = useMemo(() => {
        const safe = `${groupName || "group"}-${stageName || "stage"}`
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "");
        return `kbola-baseball-${safe || "table"}`;
    }, [groupName, stageName]);

    const hasRows = Array.isArray(standings) && standings.length > 0;

    const pct = (win, played) => {
        const p = Number(played || 0);
        const w = Number(win || 0);
        if (!p) return "0.000";
        return (w / p).toFixed(3);
    };

    const gb = (index, team) => {
        if (!hasRows || index === 0) return "-";
        const leader = standings[0];
        const leaderW = Number(leader?.win || 0);
        const leaderL = Number(leader?.lose || 0);
        const w = Number(team?.win || 0);
        const l = Number(team?.lose || 0);
        return (((leaderW - w) + (l - leaderL)) / 2).toFixed(1);
    };

    return (
        <div id={scopeId} className="card card-secondary kbola-standingsCard">
            <div className="card-header bg-kbola kbola-standingsHeader">
                <div className="d-flex align-items-center" style={{ gap: 10, minWidth: 0 }}>
                    <span className="kbola-pill kbola-pillBrand kbola-miniPill">
                        <i className="fas fa-baseball-ball" />
                        {t("baseballStandings.pill")}
                    </span>

                    <h3 className="card-title mb-0 kbola-standingsTitle" title={`${groupName} (${stageName})`}>
                        <b>
                            {groupName} ({stageName})
                        </b>
                    </h3>
                </div>

                <div className="card-tools">
                    <button
                        type="button"
                        className="btn btn-tool kbola-toolBtn"
                        data-card-widget="collapse"
                        title={t("baseballStandings.collapse")}
                        aria-label={t("baseballStandings.collapse")}
                    >
                        <i className="fas fa-minus" />
                    </button>
                </div>
            </div>

            <div className="card-body table-responsive p-0 kbola-standingsBody">
                <table className="table table-striped projects mb-0 kbola-standingsTable">
                    <thead className="thead-light kbola-standingsThead">
                    <tr>
                        <th style={{ width: "1%" }} className="kbola-th">
                            {t("baseballStandings.columns.rank")}
                        </th>
                        <th className="kbola-th">{t("baseballStandings.columns.team")}</th>
                        <th className="text-center kbola-th">{t("baseballStandings.columns.played")}</th>
                        <th className="text-center kbola-th">{t("baseballStandings.columns.win")}</th>
                        <th className="text-center kbola-th">{t("baseballStandings.columns.lose")}</th>
                        <th className="text-center kbola-th">{t("baseballStandings.columns.pct")}</th>
                        <th className="text-center kbola-th">{t("baseballStandings.columns.gb")}</th>
                        <th className="text-center kbola-th">{t("baseballStandings.columns.rs")}</th>
                        <th className="text-center kbola-th">{t("baseballStandings.columns.ra")}</th>
                        <th className="text-center kbola-th">{t("baseballStandings.columns.diff")}</th>
                    </tr>
                    </thead>

                    <tbody className="kbola-standingsTbody">
                    {hasRows ? (
                        standings.map((standing, index) => (
                            <tr key={standing?.team?.id || index} className="kbola-row">
                                <td className="kbola-td kbola-rank">{index + 1}</td>

                                <td className="kbola-td kbola-teamName" title={standing?.team?.name}>
                                    {standing?.team?.name}
                                </td>
                                <td className="text-center kbola-td">{standing?.played}</td>

                                <td className="text-center kbola-td">{standing?.win}</td>
                                <td className="text-center kbola-td">{standing?.lose}</td>

                                <td className="text-center kbola-td">{pct(standing?.win, standing?.played)}</td>
                                <td className="text-center kbola-td">{gb(index, standing)}</td>

                                <td className="text-center kbola-td">{standing?.scoreFor}</td>
                                <td className="text-center kbola-td">{standing?.scoreAgainst}</td>

                                <td className="text-center kbola-td kbola-points">
                                    {Number(standing?.scoreFor || 0) - Number(standing?.scoreAgainst || 0)}
                                </td>
                            </tr>
                        ))
                    ) : (
                        <NoData />
                    )}
                    </tbody>
                </table>
            </div>

            <style>{`
        #${scopeId}{
          font-family: 'Poppins', sans-serif;
          border-radius: 18px;
          overflow: hidden;
          border: 1px solid rgba(0,0,0,0.08);
          box-shadow: 0 18px 44px rgba(0,0,0,0.10);
          margin-bottom: 14px;
        }

        #${scopeId} i.fas,
        #${scopeId} i.far,
        #${scopeId} i.fal,
        #${scopeId} i.fa,
        #${scopeId} i[class^="fa-"],
        #${scopeId} i[class*=" fa-"] {
          font-family: "Font Awesome 5 Free" !important;
          font-weight: 900;
        }
        #${scopeId} i.fab {
          font-family: "Font Awesome 5 Brands" !important;
          font-weight: 400;
        }

        #${scopeId} .kbola-standingsHeader{
          padding: 12px 14px;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        #${scopeId} .kbola-standingsHeader:after{
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(
                135deg,
                ${alphaFromHex(colors.primary, 1.0)},
                ${alphaFromHex(colors.primary, 0.0)}
            );
          pointer-events: none;
        }
        #${scopeId} .kbola-standingsHeader > *{
          position: relative;
          z-index: 1;
        }

        #${scopeId} .kbola-standingsTitle{
          font-size: 14px;
          font-weight: 900;
          color: ${colors.textOnPrimary};
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 56vw;
        }

        #${scopeId} .kbola-pill{
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 10px;
          border-radius: 999px;
          font-weight: 900;
          font-size: 12px;
          border: 1px solid rgba(0,0,0,0.10);
          line-height: 1;
          white-space: nowrap;
        }
        #${scopeId} .kbola-miniPill{
          padding: 6px 10px;
          font-size: 11.5px;
          letter-spacing: 0.02em;
        }
        #${scopeId} .kbola-pillBrand{
          background: ${colors.secondary};
          color: ${colors.textOnSecondary};
          border-color: rgba(0,0,0,0.10);
        }

        #${scopeId} .card-tools{
          margin-left: auto;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          height: 100%;
        }
        #${scopeId} .card-tools,
        #${scopeId} .card-tools .btn-tool{
          float: none !important;
        }

        #${scopeId} .kbola-toolBtn{
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 38px;
          height: 38px;
          padding: 0;
          line-height: 1;
          border-radius: 12px;
          border: 1px solid rgba(0,0,0,0.12);
          background: ${colors.primary};
          color: ${colors.textOnPrimary};
        }
        #${scopeId} .kbola-toolBtn:hover{
          filter: brightness(1.06);
        }

        #${scopeId} .kbola-standingsBody{
          background: #fff;
        }

        #${scopeId} .kbola-standingsThead th.kbola-th{
          background: linear-gradient(135deg, rgba(var(--kbs-primary-rgb, 0,173,255), 0.18), rgba(0,0,0,0));
          border-bottom: 1px solid rgba(0,0,0,0.06);
          color: rgba(20,21,26,0.72);
          font-size: 12px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.02em;
          padding: 12px 14px;
          vertical-align: middle;
          white-space: nowrap;
        }

        #${scopeId} .kbola-standingsTbody td.kbola-td{
          border-top: 1px solid rgba(0,0,0,0.06);
          padding: 12px 14px;
          vertical-align: middle;
          font-weight: 800;
          color: rgba(20,21,26,0.88);
          white-space: nowrap;
        }

        #${scopeId} .kbola-standingsTbody tr:hover td{
          background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.10);
        }

        #${scopeId} .kbola-rank{
          color: rgba(20,21,26,0.60);
          font-weight: 900;
        }
        #${scopeId} .kbola-teamName{
          font-weight: 900;
          max-width: 340px;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        #${scopeId} .kbola-points{
          font-weight: 900;
          color: rgba(20,21,26,1);
        }

        @media (max-width: 576px){
          #${scopeId} .kbola-standingsTitle{ max-width: 48vw; }
          #${scopeId} .kbola-teamName{ max-width: 180px; }
        }
      `}</style>
        </div>
    );
}
